import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FirebaseError } from "firebase/app";
import { MarketingNav } from "../../chrome/MarketingNav";
import { useAuth } from "@/lib/auth-context";
import { afterSignInPath, nextQuery } from "@/lib/auth-return";
import { LegalNotice } from "@/components/LegalNotice";

export default function SignupPage() {
  const navigate = useNavigate();
  // `?next=` from the app guard: the page a signed-out visitor asked for (e.g. a reminder link).
  const next = useSearchParams()[0].get("next");
  const { signUp, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signUp(email, password);
      navigate(afterSignInPath(next), { replace: true });
    } catch (err) {
      if (err instanceof FirebaseError) {
        setError(signupErrorMessage(err.code));
      } else {
        setError("Ceva nu a mers. Încearcă din nou.");
      }
      setSubmitting(false);
    }
  };

  const onGoogle = async () => {
    setError(null);
    setGoogleSubmitting(true);
    try {
      await signInWithGoogle();
      navigate(afterSignInPath(next), { replace: true });
    } catch (err) {
      if (err instanceof FirebaseError) {
        if (err.code !== "auth/popup-closed-by-user" && err.code !== "auth/cancelled-popup-request") {
          setError(signupErrorMessage(err.code));
        }
      } else {
        setError("Conectarea cu Google a eșuat. Încearcă din nou.");
      }
      setGoogleSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Creează cont — Asistent Auto</title>
      </Helmet>
      <MarketingNav />
      <div className="auth-wrap">
        <div className="auth-card">
          <h1>Creează cont.</h1>
          <p className="lede">
            Cu același cont vei putea accesa și aplicația mobilă.
          </p>

          <button
            type="button"
            className="btn btn--google btn--lg auth-google"
            onClick={onGoogle}
            disabled={googleSubmitting || submitting}
          >
            <GoogleGlyph />
            {googleSubmitting ? "Conectăm…" : "Continuă cu Google"}
          </button>

          <div className="auth-divider"><span>sau</span></div>

          <form className="auth-form" onSubmit={onSubmit}>
            {error && <div className="auth-error">{error}</div>}
            <div className="auth-field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="auth-field">
              <label htmlFor="password">Parolă</label>
              <input
                id="password"
                type="password"
                required
                autoComplete="new-password"
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <button
              type="submit"
              className="btn btn--primary btn--lg"
              disabled={submitting || googleSubmitting}
            >
              {submitting ? "Creăm contul…" : "Creează cont"}
            </button>
          </form>
          <LegalNotice />
          <div className="auth-foot">
            Ai deja cont? <Link to={`/cont/login${nextQuery(next)}`}>Intră în cont</Link>
          </div>
        </div>
      </div>
    </>
  );
}

function GoogleGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      {/* design-check: allow — Google logo colours */}
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.61z" />
      {/* design-check: allow — Google logo colours */}
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.92-2.27c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.34A9 9 0 0 0 9 18z" />
      {/* design-check: allow — Google logo colours */}
      <path fill="#FBBC05" d="M3.97 10.71A5.4 5.4 0 0 1 3.68 9c0-.59.1-1.17.29-1.71V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.34z" />
      {/* design-check: allow — Google logo colours */}
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A8.99 8.99 0 0 0 9 0 9 9 0 0 0 .96 4.95l3.01 2.34C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

function signupErrorMessage(code: string): string {
  switch (code) {
    case "auth/email-already-in-use":
      return "Există deja un cont cu acest email.";
    case "auth/invalid-email":
      return "Email invalid.";
    case "auth/weak-password":
      return "Parola trebuie să aibă cel puțin 6 caractere.";
    case "auth/network-request-failed":
      return "Conexiune eșuată. Verifică internetul.";
    case "auth/account-exists-with-different-credential":
      return "Există deja un cont cu acest email, conectat altfel. Folosește metoda originală.";
    case "auth/popup-blocked":
      return "Browserul a blocat fereastra Google. Permite popup-urile și încearcă din nou.";
    default:
      return "Ceva nu a mers. Încearcă din nou.";
  }
}
